// v84.6 — Halaman GPS push utk teknisi. Public (no login).
// URL: /tech/gps?token=XXX (token per-teknisi dari technicians.gps_token).
// Auto-push GPS tiap 2 menit + on significant change.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, CheckCircle2, XCircle, Loader2, Wifi } from "lucide-react";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PUSH_INTERVAL_MS = 2 * 60 * 1000; // 2 menit

export default function TechGps() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [tech, setTech] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState("Menunggu izin GPS…");
  const [lastPush, setLastPush] = useState(null);
  const [coords, setCoords] = useState(null);
  const [pushCount, setPushCount] = useState(0);
  const lastPushRef = useRef(0);
  const watchIdRef = useRef(null);

  // 1. Validasi token
  useEffect(() => {
    if (!token) { setErr("Token GPS tidak ada di URL"); return; }
    axios.get(`${BASE}/api/public/tech-gps-info?token=${token}`)
      .then(r => setTech(r.data))
      .catch(() => setErr("Token GPS tidak valid. Hubungi admin."));
  }, [token]);

  // 2. Start GPS watch begitu tech valid + interval polling utk memaksa
  //    fresh reading (workaround watchPosition yg dipause saat tab background)
  useEffect(() => {
    if (!tech) return;
    if (!navigator.geolocation) {
      setErr("Browser tidak mendukung GPS"); return;
    }
    const watchOpts = { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 };
    const activeOpts = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

    async function pushLocation(pos) {
      const { latitude, longitude, accuracy } = pos.coords;
      setCoords({ lat: latitude, lng: longitude, accuracy });
      setStatus("GPS aktif");
      try {
        const r = await axios.post(`${BASE}/api/public/tech/location`, {
          token, lat: latitude, lng: longitude, accuracy,
        });
        lastPushRef.current = Date.now();
        setLastPush(new Date());
        setPushCount(c => c + 1);
        if (r.data?.moved_m > 0) {
          setStatus(`GPS aktif · geser ${Math.round(r.data.moved_m)}m dari sebelumnya`);
        } else {
          setStatus("GPS aktif · lokasi dikirim");
        }
      } catch (e) {
        setStatus("GPS aktif tapi gagal kirim ke server");
      }
    }

    // watchPosition — event-based (fires on movement)
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastPushRef.current > 30 * 1000 || lastPushRef.current === 0) {
          // Throttle 30s antar push dari watchPosition
          await pushLocation(pos);
        } else {
          setCoords({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        }
      },
      (e) => setErr(`GPS ditolak / error: ${e.message}`),
      watchOpts,
    );

    // v84.12 — Interval polling: force getCurrentPosition tiap 2 menit,
    // maximumAge: 0 → jangan pakai cache. Ini fallback kalau watchPosition
    // di-throttle browser (tab background, screen lock, dll).
    const activePoll = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        pushLocation,
        (e) => setStatus(`GPS refresh error: ${e.message}`),
        activeOpts,
      );
    }, PUSH_INTERVAL_MS);

    // Force refresh saat tab kembali visible
    const onVis = () => {
      if (document.visibilityState === "visible") {
        navigator.geolocation.getCurrentPosition(pushLocation, () => {}, activeOpts);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (watchIdRef.current !== null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {}
      }
      clearInterval(activePoll);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tech, token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7] p-4" data-testid="tech-gps-page">
      <div className="max-w-md mx-auto space-y-3">
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">GLOBAL AC</div>
          <div className="text-[16px] font-bold text-zinc-800 mt-0.5">GPS Teknisi</div>
          {tech && (
            <div className="mt-2 text-[13px] text-zinc-700" data-testid="tech-gps-name">
              Halo, <span className="font-semibold">{tech.tech_name}</span> 👋
            </div>
          )}
          <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
            Halaman ini akan mengirim lokasi GPS Anda ke sistem otomatis tiap 2 menit selama halaman terbuka.
            <br />Cukup buka dari HP saat mulai kerja, kunci di background HP boleh.
          </div>
        </div>

        {err ? (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4" data-testid="tech-gps-error">
            <XCircle className="w-8 h-8 text-red-600 mb-1" />
            <div className="text-[13px] font-semibold text-red-800">{err}</div>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-zinc-800">
                {coords ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
                <span data-testid="tech-gps-status">{status}</span>
              </div>
              {coords && (
                <div className="mt-3 space-y-1 text-[12px] text-zinc-700 font-mono">
                  <div>Lat: {coords.lat.toFixed(6)}</div>
                  <div>Lng: {coords.lng.toFixed(6)}</div>
                  <div>Akurasi: ±{Math.round(coords.accuracy)} m</div>
                </div>
              )}
              {lastPush && (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <Wifi className="w-3 h-3 text-emerald-600" />
                  Terakhir dikirim: {lastPush.toLocaleTimeString("id-ID", { timeZone: "Asia/Makassar" })} WITA
                  <span className="ml-auto text-zinc-400">({pushCount}x)</span>
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-900 leading-relaxed">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 flex-none mt-0.5" />
                <div>
                  <div className="font-semibold">Penting:</div>
                  <ul className="list-disc list-inside space-y-0.5 mt-1">
                    <li>Jangan tutup tab ini selama bekerja</li>
                    <li>Aktifkan lokasi HP (Setting → Location)</li>
                    <li>Refresh halaman kalau GPS tampak stuck</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
